import {
  AssignmentStatusByStudentItem,
  AssignmentStatusDetailByStudentItem,
  AssignmentOrderByType,
} from '../../../../constants/types/lms/assignment/type';
import TextWinds from '../../../common/TextWinds';
import {
  Button,
  DatePicker,
  Input,
  Pagination,
  PaginationProps,
  Select,
} from 'antd';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  deleteAssignmentBulk,
  getAssignmentStatusDetailListByStudent,
} from '../../../../api/utils/lms/assignment/api';
import { useAppDispatch, useAppSelector } from '../../../../store/hook';
import { getErrorMessages } from '../../../../api/utils/util-func';
import { openAlert, openConfirm } from '../../../../store/features/modalSlice';
import AssignmentStatusByStudentCard from './AssignmentStatusByStudentCard';
import { numberWithCommas } from '../../../../api/utils/format/numbers';
import {
  DEFAULT_PAGINATION,
  PAGE,
  PAGE_SIZE,
} from '../../../../constants/constants';
import { selectLmsLists } from '../../../../store/features/lmsSlice';
import {
  AssignmentStatusType,
  AssignmentType,
  CourseId,
} from '../../../../constants/types/lms/type';

interface StatusByStudentContentsProps {
  selectedStudent: AssignmentStatusByStudentItem;
  handleBack: () => void;
}

type AssignmentViewType = 'LIST' | 'CARD';

const ASSIGNMENT_DATE_TYPE_OPTIONS = [
  {
    label: '내준 날짜',
    value: 'ASSIGNED',
  },
  {
    label: '완료일',
    value: 'COMPLETED',
  },
];

const ASSIGNMENT_STATUS_OPTIONS: {
  label: string;
  value: AssignmentStatusType | 'ALL';
}[] = [
  { label: '전체', value: 'ALL' },
  { label: '시작 전', value: AssignmentStatusType.NOT_STARTED },
  { label: '진행 중', value: AssignmentStatusType.IN_PROGRESS },
  { label: '완료', value: AssignmentStatusType.COMPLETED },
  { label: '마감', value: AssignmentStatusType.CLOSED },
];

/**
 * 개편 LMS (교사) - 과제 관리 > 과제 현황 페이지 > 학생별 과제 현황
 */
const StatusByStudentContents = ({
  selectedStudent,
  handleBack,
}: StatusByStudentContentsProps) => {
  const { classId } = useParams();

  const lmsLists = useAppSelector(selectLmsLists);

  const courseType = useMemo(() => {
    switch (lmsLists.course.id) {
      case CourseId.BASIC:
        return 'BASIC';
      case CourseId.WMW:
        return 'WMW';
      case CourseId.HFHJ:
        return 'HFHJ';
      default:
        return null;
    }
  }, [lmsLists]);

  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    startDate,
    endDate,
    dateType,
    orderBy,
    assignmentStatus,
    page,
    pageSize,
  } = useMemo(() => {
    return {
      startDate: searchParams.get('startDate') || undefined,
      endDate: searchParams.get('endDate') || undefined,
      dateType: searchParams.get('dateType') || 'ASSIGNED',
      orderBy: searchParams.get('orderBy') || undefined,
      assignmentStatus: searchParams.get('assignmentStatus') || 'ALL',
      page: +searchParams.get('page') || PAGE,
      pageSize: +searchParams.get('pageSize') || PAGE_SIZE,
    };
  }, [searchParams]);

  const [searchOption, setSearchOption] = useState('book');
  const [searchValue, setSearchValue] = useState('');

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [assignmentStatusList, setAssignmentStatusList] = useState<
    AssignmentStatusDetailByStudentItem[]
  >([]);
  const [pageInfo, setPageInfo] = useState<PaginationProps>(DEFAULT_PAGINATION);

  const [selectedAssignmentIds, setSelectedAssignmentIds] = useState<number[]>(
    [],
  );

  const [viewType, setViewType] = useState<AssignmentViewType>('CARD');

  const fetchAssignmentStatusList = async () => {
    if (!selectedStudent) return;

    try {
      setIsLoading(true);

      const response = await getAssignmentStatusDetailListByStudent({
        classId,
        detailId: selectedStudent.id,
        page,
        pageSize,
        toDoStatus:
          assignmentStatus === 'ALL'
            ? undefined
            : AssignmentStatusType[assignmentStatus as AssignmentStatusType],
        toDoType:
          courseType === 'BASIC'
            ? AssignmentType.JEBSON
            : AssignmentType.JEBSPL,
        ...(searchValue
          ? {
              [searchOption]: searchValue,
            }
          : {}),
        fromDate: startDate,
        toDate: endDate,
        dateType: dateType as 'ASSIGNED' | 'COMPLETED',
        orderBy: AssignmentOrderByType[orderBy as AssignmentOrderByType],
      });

      setAssignmentStatusList(response.result ?? []);
      setPageInfo(response.page);
      setSelectedAssignmentIds([]);
    } catch (error) {
      dispatch(openAlert({ message: getErrorMessages(error) }));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedStudent) return;

    const isDateRangeValid = (startDate && !endDate) || (!startDate && endDate);

    if (isDateRangeValid) return;

    fetchAssignmentStatusList();
  }, [
    selectedStudent,
    startDate,
    endDate,
    dateType,
    orderBy,
    assignmentStatus,
    page,
    pageSize,
  ]);

  const handleSearch = async () => {
    await fetchAssignmentStatusList();
  };

  const handleSortToggle = (
    nextOrderBy: keyof typeof AssignmentOrderByType,
  ) => {
    if (orderBy === nextOrderBy) {
      searchParams.delete('orderBy');
      setSearchParams(searchParams);
    } else {
      searchParams.set('orderBy', nextOrderBy);
      setSearchParams(searchParams);
    }
  };

  const handleAssignmentCheckChange = (
    assignmentId: number,
    checked: boolean,
  ) => {
    setSelectedAssignmentIds((prev) => {
      if (checked) {
        return prev.includes(assignmentId) ? prev : [...prev, assignmentId];
      }

      return prev.filter((id) => id !== assignmentId);
    });
  };

  const handleDeleteSelectedAssignments = () => {
    if (selectedAssignmentIds.length <= 0) {
      dispatch(
        openAlert({
          message: '삭제할 과제를 선택해 주세요.',
        }),
      );
      return;
    }

    dispatch(
      openConfirm({
        message: (
          <div className="flex flex-col md:my-3">
            <div className="text-base">
              선택한 과제 {selectedAssignmentIds.length}건을 삭제합니다.
            </div>

            <div className="text-base">삭제하시면 되돌릴 수 없습니다.</div>

            <div className="text-base">정말 삭제하시겠습니까?</div>
          </div>
        ),
        confirmText: '삭제하기',
        cancelText: '닫기',
        onConfirm: async () => {
          try {
            await deleteAssignmentBulk({
              classId,
              assignedIds: selectedAssignmentIds,
            });

            setSelectedAssignmentIds([]);
            await fetchAssignmentStatusList();
          } catch (error) {
            dispatch(openAlert({ message: getErrorMessages(error) }));
          }
        },
      }),
    );
  };

  return (
    <>
      <div className="mb-5 flex items-center gap-3">
        <Button
          type="default"
          className="flex shrink-0 items-center gap-2 rounded-lg px-5"
          onClick={() => {
            handleBack();
            setSearchParams({});
          }}
        >
          ← 학생 목록으로
        </Button>

        <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <TextWinds type="title_h3" className="min-w-0 truncate">
            {selectedStudent.name}
          </TextWinds>

          <div className="flex shrink-0 items-center gap-2">
            {courseType !== 'BASIC' && (
              <div className="flex items-center gap-1 rounded-full border border-amber-400 bg-amber-100 px-2.5 py-0.5">
                <TextWinds
                  type="caption1"
                  className="hidden font-bold text-amber-800 xl:inline"
                >
                  🌟 달란트
                </TextWinds>

                <TextWinds type="caption1" className="font-bold text-amber-800">
                  {numberWithCommas(selectedStudent.dalantTotal ?? 0)}개
                </TextWinds>
              </div>
            )}

            <div className="hidden items-center rounded-lg border border-gray3 bg-white p-0.5 md:flex">
              <button
                type="button"
                title="목록형으로 보기"
                className={[
                  'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                  viewType === 'LIST'
                    ? 'bg-purple1 text-purple5'
                    : 'text-gray5 hover:bg-gray1 hover:text-gray7',
                ].join(' ')}
                onClick={() => setViewType('LIST')}
              >
                <ListBulletIcon className="h-4 w-4" />
              </button>

              <button
                type="button"
                title="카드형으로 보기"
                className={[
                  'flex h-8 w-8 items-center justify-center rounded-md transition-colors',
                  viewType === 'CARD'
                    ? 'bg-purple1 text-purple5'
                    : 'text-gray5 hover:bg-gray1 hover:text-gray7',
                ].join(' ')}
                onClick={() => setViewType('CARD')}
              >
                <Squares2X2Icon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="mb-5 flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
        <div className="flex min-w-0 flex-col gap-3 xl:flex-row xl:flex-wrap xl:items-center">
          <div className="flex w-full gap-2 xl:w-auto">
            <Select
              className="w-[100px] shrink-0"
              options={[
                { value: 'book', label: '북' },
                { value: 'lesson', label: '레슨' },
                { value: 'title', label: '제목' },
                { value: 'name', label: '학생이름' },
                { value: 'uid', label: '학생ID' },
              ]}
              value={searchOption}
              onChange={(v) => setSearchOption(v)}
            />

            <Input
              type="search"
              className="min-w-0 flex-1 xl:w-[200px] xl:flex-none"
              placeholder="검색어를 입력하세요"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onPressEnter={handleSearch}
              allowClear
            />

            <Button
              className="shrink-0 rounded-lg border-purple5 bg-purple5 text-white hover:!border-purple6 hover:!bg-purple6 hover:!text-white active:!border-purple7 active:!bg-purple7"
              onClick={handleSearch}
            >
              검색
            </Button>
          </div>

          {/* 모바일 날짜 필터 */}
          <div className="flex flex-col gap-2 xl:hidden">
            <Select
              className="w-full"
              placeholder="날짜 기준"
              options={ASSIGNMENT_DATE_TYPE_OPTIONS}
              value={dateType}
              onChange={(value) => {
                const nextParams = new URLSearchParams(searchParams);

                nextParams.set('dateType', value);
                nextParams.set('page', '1');

                setSearchParams(nextParams);
              }}
            />

            <div className="grid grid-cols-2 gap-2">
              <DatePicker
                className="w-full rounded-lg"
                placeholder="시작일"
                value={startDate ? dayjs(startDate) : undefined}
                placement="bottomLeft"
                onChange={(date) => {
                  const nextStartDate = date?.format('YYYY-MM-DD') ?? '';

                  searchParams.set('startDate', nextStartDate);
                  setSearchParams(searchParams);
                }}
                disabledDate={(current) => {
                  if (!endDate) return false;
                  return current.isAfter(endDate, 'day');
                }}
              />

              <DatePicker
                className="w-full rounded-lg"
                placeholder="종료일"
                value={endDate ? dayjs(endDate) : undefined}
                placement="bottomRight"
                onChange={(date) => {
                  const nextEndDate = date?.format('YYYY-MM-DD') ?? '';

                  searchParams.set('endDate', nextEndDate);
                  setSearchParams(searchParams);
                }}
                disabledDate={(current) => {
                  if (!startDate) return false;
                  return current.isBefore(startDate, 'day');
                }}
              />
            </div>
          </div>

          {/* PC 날짜 필터 */}
          <div className="hidden items-center gap-2 xl:flex">
            <Select
              className="w-[110px] shrink-0"
              placeholder="날짜 기준"
              options={ASSIGNMENT_DATE_TYPE_OPTIONS}
              value={dateType}
              onChange={(value) => {
                const nextParams = new URLSearchParams(searchParams);

                nextParams.set('dateType', value);
                nextParams.set('page', '1');

                setSearchParams(nextParams);
              }}
            />

            <DatePicker.RangePicker
              className="w-[260px] rounded-lg"
              placeholder={['시작일', '종료일']}
              value={
                startDate && endDate
                  ? [dayjs(startDate), dayjs(endDate)]
                  : [undefined, undefined]
              }
              onChange={(dates) => {
                searchParams.set(
                  'startDate',
                  dates?.[0]?.format('YYYY-MM-DD') ?? '',
                );
                searchParams.set(
                  'endDate',
                  dates?.[1]?.format('YYYY-MM-DD') ?? '',
                );
                setSearchParams(searchParams);
              }}
            />
          </div>

          <div className="grid w-full grid-cols-2 gap-2 xl:flex xl:w-auto xl:items-center">
            <Button
              type="default"
              className={[
                'rounded-lg',
                orderBy === AssignmentOrderByType.LATEST
                  ? '!border-purple5 !bg-purple1 !text-purple5'
                  : '!border-gray3 !bg-white hover:!border-purple3 hover:!bg-gray0',
              ].join(' ')}
              onClick={() => handleSortToggle(AssignmentOrderByType.LATEST)}
            >
              <span className="flex items-center justify-center gap-0.5">
                <ArrowDownIcon className="h-4 w-4" />
                최신순
              </span>
            </Button>

            <Button
              type="default"
              className={[
                'rounded-lg',
                orderBy === AssignmentOrderByType.INCOMPLETE_FIRST
                  ? '!border-purple5 !bg-purple1 !text-purple5'
                  : '!border-gray3 !bg-white hover:!border-purple3 hover:!bg-gray0',
              ].join(' ')}
              onClick={() =>
                handleSortToggle(AssignmentOrderByType.INCOMPLETE_FIRST)
              }
            >
              <span className="flex items-center justify-center gap-0.5">
                <ArrowUpIcon className="h-4 w-4" />
                미완료순
              </span>
            </Button>
          </div>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-2 xl:flex-nowrap">
          {ASSIGNMENT_STATUS_OPTIONS.map((option) => {
            const isActive = assignmentStatus === option.value;

            return (
              <button
                key={option.value}
                type="button"
                className={[
                  'flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 transition-colors',
                  isActive
                    ? 'border-purple5 bg-purple5'
                    : 'border-gray4 bg-white hover:bg-gray1',
                ].join(' ')}
                onClick={() => {
                  const nextParams = new URLSearchParams(searchParams);

                  nextParams.set('assignmentStatus', option.value);
                  nextParams.set('page', '1');

                  setSearchParams(nextParams);
                }}
              >
                <TextWinds type="caption1" color={isActive ? 'white' : 'gray8'}>
                  {option.label}
                </TextWinds>
              </button>
            );
          })}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-3 xl:pt-1">
          <TextWinds type="caption1" color="gray7">
            조회 결과 :{' '}
            <span className="font-bold text-gray9">{pageInfo.total}</span>개
          </TextWinds>

          <Button
            type="default"
            danger
            disabled={selectedAssignmentIds.length <= 0}
            className={[
              'rounded-lg px-3 font-bold',
              selectedAssignmentIds.length > 0
                ? '!border-red-300 !bg-white !text-red-500 hover:!border-red-400 hover:!bg-red-50 hover:!text-red-600'
                : '!cursor-not-allowed !border-gray3 !bg-gray1 !text-gray5',
            ].join(' ')}
            onClick={handleDeleteSelectedAssignments}
          >
            전체 삭제
            {selectedAssignmentIds.length > 0 &&
              ` (${selectedAssignmentIds.length})`}
          </Button>
        </div>
      </div>

      {!isLoading && assignmentStatusList.length === 0 ? (
        <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-gray4 bg-gray0 px-4 text-center">
          <TextWinds type="content_body2" color="gray6">
            조회된 과제 현황이 없습니다.
          </TextWinds>
        </div>
      ) : (
        <>
          <div
            className={[
              viewType === 'LIST'
                ? 'flex flex-col gap-4'
                : 'grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3',
            ].join(' ')}
          >
            {assignmentStatusList.map((assignment) => (
              <AssignmentStatusByStudentCard
                key={assignment.id}
                selectedStudent={selectedStudent}
                assignment={assignment}
                checked={selectedAssignmentIds.includes(assignment.id)}
                onCheckedChange={handleAssignmentCheckChange}
                fetchList={fetchAssignmentStatusList}
              />
            ))}
          </div>

          <div className="mt-6 flex justify-center">
            <Pagination
              current={page}
              pageSize={pageSize}
              total={pageInfo.total}
              showSizeChanger
              onChange={(page, pageSize) => {
                searchParams.set('page', page.toString());
                searchParams.set('pageSize', pageSize.toString());
                setSearchParams(searchParams);

                window.scrollTo({
                  top: 0,
                  behavior: 'smooth',
                });
              }}
            />
          </div>
        </>
      )}
    </>
  );
};

export default StatusByStudentContents;
